import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { partial, isNumber } from 'lodash';
import {
  userSettingsLabels,
  PatientMessagingAdminLockSource,
} from '../UserSettingsForm/UserSettings';
import AccessCheckbox from './AccessCheckbox';
import SettingsGroup, { visibleSettingKeys } from './SettingsGroup';
import styles from './UserSettingsAndAccessForm.module.css';
import type {
  PermissionMap,
  UserSettings,
  UserOrganizationSettings,
  Persona,
  UserData,
} from 'admin/types';
import { BlockedByPersonaText } from 'admin/utils/shouldRender';
import { getMostRestrictiveValue } from 'admin/utils/getMostRestrictiveValue';
import { hasOrgFeatureOrSettings } from 'admin/utils/renderSettingConditions';
import { PermissionKey } from 'js-sdk/src/types/Persona';
import { mobxInjectSelect } from 'common/utils';
import { DotsIndicator } from 'common/components';
import { useAppSelector } from 'redux-stores';
import TCClient from 'client';

type BoolUserSettings = Omit<
  UserSettings,
  | 'id'
  | 'autoLogout'
  | 'personaId'
  | 'personaName'
  | 'personaMappingExclusion'
  | 'pinLock'
  | 'messageLifespan'
  | 'messageLifespanOptIn'
  | 'pinLockOptIn'
>;

type NumericUserSettings = Pick<UserSettings, 'pinLock' | 'messageLifespan'>;

type Settings = BoolUserSettings &
  NumericUserSettings & {
    messageLifespanOptIn?: boolean;
    pinLockOptIn?: boolean;
  };

type MobxProps = {
  featureFlags: Record<string, unknown>;
  findPersona: (props: {
    personaId: string;
    selectedOrganizationId: string;
  }) => Promise<Persona | undefined>;
  getURLParams: () => Record<string, string>;
  isPersonaMessageTtlAndPinLockEnabled: boolean;
  isPmaPatientEngagementEnabled: boolean;
  loadPermissions: (
    selectedOrganizationId: string
  ) => Promise<{ name: PermissionKey; description: string }[]>;
};

type UserSettingsAndAccessFormProps = {
  disableAll?: boolean;
  isPersonaForm?: boolean;
  userPermissions: PermissionMap | undefined;
  userSettings: UserSettings;
  setUserSettings: React.Dispatch<React.SetStateAction<UserSettings | undefined>>;
  setUserPermissions: React.Dispatch<React.SetStateAction<PermissionMap | undefined>>;
  userData?: UserData;
  // Increments after each successful save, so the form can forget values it remembered before.
  savedVersion?: number;
};

const getUserPersonas = (userData: UserData, organizationId: string) => {
  const rawUserPersonas = userData?.organizationSettings.find(
    (org) => org.organizationId === organizationId
  );
  let userPersonas = null;
  if (rawUserPersonas?.permission_groups) {
    if (!Array.isArray(rawUserPersonas?.permission_groups)) {
      userPersonas = [rawUserPersonas?.permission_groups];
    } else {
      userPersonas = rawUserPersonas?.permission_groups;
    }
  }
  return userPersonas;
};

function UserSettingsAndAccessForm({
  disableAll = false,
  featureFlags,
  isPersonaForm = false,
  isPersonaMessageTtlAndPinLockEnabled,
  isPmaPatientEngagementEnabled,
  getURLParams,
  loadPermissions,
  userPermissions = {},
  userSettings,
  setUserSettings,
  setUserPermissions,
  userData,
  savedVersion,
}: UserSettingsAndAccessFormProps & MobxProps) {
  const [userOrgSettings, setUserOrgSettings] = useState<UserOrganizationSettings>();
  const [orgPermissions, setOrgPermissions] = useState<
    Map<PermissionKey, { name: string; description: string }>
  >(new Map());
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const organizationId = useAppSelector((state) => state.admin.selectedOrganizationId);
  const urlParams = getURLParams();

  useEffect(() => {
    TCClient.adminUsers
      .fetchOrganizationSettings({ organizationId })
      .then((org_settings) => setUserOrgSettings(org_settings));
    loadPermissions(organizationId).then((permissions) =>
      setOrgPermissions(new Map(permissions.map((p) => [p.name, p])))
    );
  }, [loadPermissions, organizationId]);

  useEffect(() => {
    if (userPermissions && Object.keys(userPermissions).length > 0) {
      setPermissions(userPermissions);
    }
  }, [isPersonaForm, orgPermissions, userPermissions]);

  const { permissions: personasPermissions, settings: personasSettings } = useMemo(() => {
    const permissions = {} as Record<PermissionKey, boolean>;
    const settings: Settings = {} as Settings;
    // Excluded users don't inherit anything from their personas.
    if (userData && !(isPmaPatientEngagementEnabled && userSettings?.personaMappingExclusion)) {
      const userPersonas = getUserPersonas(userData, organizationId);
      userPersonas?.forEach((persona) => {
        Object.keys(persona?.details?.permissions || {}).forEach((permission) => {
          if (!permissions[permission as PermissionKey]) {
            permissions[permission as PermissionKey] =
              persona?.details?.permissions?.[permission] || false;
          }
        });
        const personaSettings = persona?.details?.settings as unknown as Settings;
        if (personaSettings) {
          Object.keys(personaSettings).forEach((setting) => {
            if (!['pinLock', 'messageLifespan'].includes(setting)) {
              const key = setting as keyof BoolUserSettings;
              if (!settings[key]) {
                settings[key] = !!personaSettings[key];
              }
            } else {
              const key = setting as keyof NumericUserSettings;
              if (!personaSettings.messageLifespanOptIn && key === 'messageLifespan') return;
              if (!personaSettings.pinLockOptIn && key === 'pinLock') return;
              if (settings[key] === undefined) {
                settings[key] = personaSettings[key];
              } else {
                settings[key] = getMostRestrictiveValue(settings[key], personaSettings[key]);
              }
            }
          });
        }
      });
    }
    return { permissions, settings };
  }, [
    organizationId,
    userData,
    userSettings?.personaMappingExclusion,
    isPmaPatientEngagementEnabled,
  ]);
  const isLockedByPersona = useCallback(
    (type: 'permissions' | 'settings', title: string) => {
      if (!userData || !getUserPersonas(userData, organizationId)?.length) return false;
      if (userSettings.personaMappingExclusion) return false;
      if (type === 'permissions') {
        return !!personasPermissions?.[title as PermissionKey];
      } else {
        if (['pinLock', 'messageLifespan'].includes(title)) {
          return !isPersonaMessageTtlAndPinLockEnabled
            ? false
            : isNumber(personasSettings?.[title as keyof NumericUserSettings]);
        } else {
          return !!personasSettings?.[title as keyof BoolUserSettings];
        }
      }
    },
    [
      organizationId,
      personasPermissions,
      personasSettings,
      userData,
      userSettings.personaMappingExclusion,
      isPersonaMessageTtlAndPinLockEnabled,
    ]
  );

  // Patient Messaging Admin from a persona or from the user's own access locks every patient
  // engagement setting; "Locked by Persona" is added per setting by userSettingsLabels.
  const patientMessagingAdminLock: PatientMessagingAdminLockSource =
    !isPmaPatientEngagementEnabled || !userSettings
      ? false
      : isLockedByPersona('permissions', 'patient_messaging_admin')
      ? 'persona'
      : userPermissions['patient_messaging_admin' as keyof PermissionMap]
      ? 'user'
      : false;

  // Visible patient engagement settings Patient Messaging Admin controls. Persona-locked
  // settings keep the persona's value.
  const getPatientEngagementKeys = (): Array<keyof UserSettings> => {
    if (!userSettings || !userOrgSettings) return [];
    const { patientEngagementSettings: visibleSettings } = userSettingsLabels(
      { ...userSettings, patientEngagementSetting: true },
      userOrgSettings,
      setUserSettings,
      featureFlags,
      urlParams,
      disableAll,
      'v2'
    );
    return visibleSettingKeys(visibleSettings, userOrgSettings).filter(
      (key) => !isLockedByPersona('settings', key)
    );
  };

  // While the user's own Patient Messaging Admin is on, keep the saved values in line with the
  // checked display, including on load, so saving sends them even if the server doesn't derive
  // them. Persona-granted Patient Messaging Admin is the persona's to save, so nothing is written
  // to the user's profile for it.
  const seededOriginalValues = useRef<Partial<UserSettings>>({});
  const unsyncedPatientEngagementKeys =
    patientMessagingAdminLock === 'user'
      ? getPatientEngagementKeys()
          .filter((key) => userSettings[key] !== true)
          .join(',')
      : '';
  useEffect(() => {
    if (!unsyncedPatientEngagementKeys) return;
    const keys = unsyncedPatientEngagementKeys.split(',') as Array<keyof UserSettings>;
    setUserSettings((prev) => {
      if (!prev) return prev;
      // Values already on at load aren't remembered: the server may have forced them on for
      // Patient Messaging Admin, so unchecking it clears them.
      keys.forEach((key) => {
        if (!(key in seededOriginalValues.current)) {
          seededOriginalValues.current = { ...seededOriginalValues.current, [key]: prev[key] };
        }
      });
      return { ...prev, ...Object.fromEntries(keys.map((key) => [key, true])) };
    });
  }, [unsyncedPatientEngagementKeys, setUserSettings]);

  // After a save, the saved values are the new baseline: unchecking then works as if the user had
  // just been opened.
  useEffect(() => {
    seededOriginalValues.current = {};
  }, [savedVersion]);

  // If that lock goes away without Patient Messaging Admin being unchecked (for example, its
  // source changes), put back the values seeded on load instead of saving them.
  const isUserPmaLock = patientMessagingAdminLock === 'user';
  useEffect(() => {
    if (isUserPmaLock) return;
    const originalValues = seededOriginalValues.current;
    if (!Object.keys(originalValues).length) return;
    seededOriginalValues.current = {};
    setUserSettings((prev) => (prev ? { ...prev, ...originalValues } : prev));
  }, [isUserPmaLock, setUserSettings]);

  if (!userSettings || !userOrgSettings) {
    return (
      <div className={classNames(styles.loadingHolder)}>
        <DotsIndicator color="#0e0d0d" size={12} marginTop={30} marginRight={5} />
      </div>
    );
  }

  const { settings, patientEngagementSettings } = userSettingsLabels(
    userSettings,
    userOrgSettings,
    setUserSettings,
    featureFlags,
    urlParams,
    disableAll,
    'v2',
    partial(isLockedByPersona, 'settings'),
    false,
    patientMessagingAdminLock
  );

  const handlePermissionClick = (permission: string, checked: boolean) => {
    setUserPermissions((prev) => ({ ...prev, [permission]: checked }));
    if (!isPmaPatientEngagementEnabled || permission !== 'patient_messaging_admin') return;

    const keys = getPatientEngagementKeys();
    if (checked) {
      // Remember each value from before Patient Messaging Admin turned it on.
      keys.forEach((key) => {
        if (!(key in seededOriginalValues.current)) {
          seededOriginalValues.current = {
            ...seededOriginalValues.current,
            [key]: userSettings[key],
          };
        }
      });
      setUserSettings((prev) =>
        prev ? { ...prev, ...Object.fromEntries(keys.map((key) => [key, true])) } : prev
      );
      return;
    }
    // Put back what was there before; settings with nothing remembered are turned off.
    const originalValues = seededOriginalValues.current;
    seededOriginalValues.current = {};
    setUserSettings((prev) =>
      prev
        ? { ...prev, ...Object.fromEntries(keys.map((key) => [key, false])), ...originalValues }
        : prev
    );
  };

  const settingsToRemove = isPersonaMessageTtlAndPinLockEnabled
    ? []
    : ['Message Lifespan', 'PIN Lock (mobile)'];

  const filteredSettings = isPersonaForm
    ? settings.filter((setting) => !settingsToRemove.includes(setting.label))
    : settings;

  return (
    <div className={styles.userSettingsAndAccessForm}>
      <hr className={classNames(styles.personaHr)} />
      <div className={styles.settingsGroup}>
        <SettingsGroup
          isPersonaForm={isPersonaForm}
          title="General Settings"
          settings={filteredSettings}
          userOrgSettings={userOrgSettings}
        />
        {hasOrgFeatureOrSettings(userOrgSettings, ['featurePatientEngagement']) ? (
          <SettingsGroup
            isPersonaForm={isPersonaForm}
            title="Patient Engagement Settings"
            settings={patientEngagementSettings}
            userOrgSettings={userOrgSettings}
          />
        ) : null}
      </div>
      <hr className={classNames(styles.personaHr)} />
      <div className={styles.settingsGroup}>
        <div className={styles.settingsGroupTitle}>Access</div>
        <div className={styles.settingsGroupGrid}>
          {Object.keys(permissions).map((permission) => {
            const lockedByPersona = isLockedByPersona('permissions', permission);
            return (
              <AccessCheckbox
                key={permission}
                isLockedByPersona={lockedByPersona}
                permission={permission}
                blockedByPersonaText={BlockedByPersonaText}
                orgPermissions={orgPermissions}
                disableAll={disableAll}
                checked={
                  permissions[permission as keyof PermissionMap] ||
                  personasPermissions[permission as PermissionKey]
                }
                onClick={(checked: boolean) => handlePermissionClick(permission, checked)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default mobxInjectSelect<UserSettingsAndAccessFormProps, MobxProps>({
  featureStore: ['featureFlags', 'getURLParams'],
  messengerStore: ['isPersonaMessageTtlAndPinLockEnabled', 'isPmaPatientEngagementEnabled'],
  personaStore: ['findPersona', 'loadPermissions'],
})(UserSettingsAndAccessForm);
